// File: frontend/src/pages/About.tsx
import React from 'react';
import SEOHead from '../components/SEOHead';
import { 
  Box, 
  Container, 
  Paper, 
  Typography, 
  styled,
  alpha,
  Fade,
  Stack,
  Chip,
  Link
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupsIcon from '@mui/icons-material/Groups';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import ShieldIcon from '@mui/icons-material/Shield';
import BusinessIcon from '@mui/icons-material/Business';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ScienceIcon from '@mui/icons-material/Science';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import XIcon from '@mui/icons-material/X';
import { cyber } from '../theme';

// Styled Components
const HeroSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${cyber.bgPaper} 0%, #3a2d6e 55%, ${cyber.primary} 130%)`,
  border: `1px solid ${alpha(cyber.primary, 0.35)}`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-10%',
    width: '300px',
    height: '300px',
    background: alpha(cyber.primaryLight, 0.25),
    borderRadius: '50%',
    filter: 'blur(80px)',
  }
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.25s ease',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: cyber.glow,
    borderColor: alpha(cyber.primary, 0.6),
  }
}));

const UseCaseCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  background: alpha(theme.palette.primary.main, 0.05),
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.1),
    transform: 'translateX(4px)',
  }
}));

const SubFeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  background: alpha(theme.palette.background.default, 0.5),
  borderRadius: theme.spacing(1),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)}, ${alpha(theme.palette.secondary.main, 0.2)})`,
  marginBottom: theme.spacing(2),
}));

const SectionCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    marginBottom: theme.spacing(3),
    background: alpha(theme.palette.background.paper, 0.8),
    backdropFilter: 'blur(10px)',
    borderRadius: theme.spacing(2),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const About = () => {
    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
            <SEOHead
                title="About TI Mindmap HUB - Independent AI Research Project"
                description="Learn about TI Mindmap HUB, an independent research project exploring AI-powered cyber threat intelligence. Understand our mission, methodology, and the team behind the platform."
                canonical="/about"
            />
            
            <Container maxWidth="lg">
                {/* Hero Section */}
                <Fade in timeout={500}>
                    <HeroSection>
                        <Box sx={{ position: 'relative', zIndex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <PsychologyIcon sx={{ fontSize: 48 }} />
                                <Typography variant="h3" component="h1" fontWeight="bold">
                                    About TI Mindmap HUB
                                </Typography>
                            </Box>
                            <Typography variant="h6" sx={{ mb: 2, opacity: 0.95, maxWidth: '900px' }}>
                                An independent research platform exploring Generative AI applications in Cyber Threat Intelligence analysis.
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip 
                                    icon={<ScienceIcon sx={{ color: 'white !important' }} />}
                                    label="Research Project" 
                                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', fontWeight: 'bold' }} 
                                />
                                <Chip 
                                    label="Independent" 
                                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', fontWeight: 'bold' }} 
                                />
                                <Chip 
                                    label="Non-Commercial" 
                                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', fontWeight: 'bold' }} 
                                />
                            </Stack>
                        </Box>
                    </HeroSection>
                </Fade>

                {/* About the Project Section */}
                <Fade in timeout={600}>
                    <SectionCard elevation={2} sx={{ mb: 4 }}>
                        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
                            <Box sx={{
                                width: 56,
                                height: 56,
                                borderRadius: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: `linear-gradient(135deg, ${alpha(cyber.primary, 0.2)}, ${alpha(cyber.primaryLight, 0.2)})`,
                            }}>
                                <VerifiedUserIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                            </Box>
                            <Box>
                                <Typography variant="h5" fontWeight="bold" gutterBottom>
                                    About the Project
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                                    TI Mindmap HUB is a <strong>personal, independent research project</strong> created to explore the intersection of Generative AI and Cyber Threat Intelligence.
                                </Typography>
                            </Box>
                        </Stack>

                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                bgcolor: alpha(cyber.primary, 0.05),
                                border: `1px solid ${alpha(cyber.primary, 0.2)}`,
                                borderRadius: 2,
                                mb: 3
                            }}
                        >
                            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                                This platform is <strong>not affiliated with, endorsed by, or representative of any employer, organization, or commercial entity</strong>. All views, methodologies, and outputs are the author's own and reflect independent research interests.
                            </Typography>
                        </Paper>

                        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
                            Research Objectives
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.8 }}>
                            The project aims to understand both the capabilities and limitations of applying Large Language Models to security workflows, with focus on:
                        </Typography>
                        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
                            <SubFeatureItem>
                                <Typography variant="body2">• Accuracy of automated IOC extraction</Typography>
                            </SubFeatureItem>
                            <SubFeatureItem>
                                <Typography variant="body2">• STIX 2.1 generation from unstructured text</Typography>
                            </SubFeatureItem>
                            <SubFeatureItem>
                                <Typography variant="body2">• LLM limitations and failure modes</Typography>
                            </SubFeatureItem>
                            <SubFeatureItem>
                                <Typography variant="body2">• Human-AI collaboration in analysis</Typography>
                            </SubFeatureItem>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 3, fontStyle: 'italic' }}>
                            The STIX 2.1 generation research has been published in the <Link href="/research#publications" color="primary">IEEE Consumer Electronics Magazine</Link>. For complete details on our research focus, methodology, and limitations, please visit the <Link href="/research" color="primary">Research page</Link>.
                        </Typography>
                    </SectionCard>
                </Fade>

                {/* Maintainer Section */}
                <Fade in timeout={650}>
                    <SectionCard elevation={2} sx={{ mb: 4 }}>
                        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
                            <Box sx={{
                                width: 56,
                                height: 56,
                                borderRadius: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: `linear-gradient(135deg, ${alpha(cyber.primary, 0.2)}, ${alpha(cyber.primaryLight, 0.2)})`,
                            }}>
                                <PersonIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                            </Box>
                            <Box>
                                <Typography variant="h5" fontWeight="bold" gutterBottom>
                                    Project Maintainer
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                                    TI Mindmap HUB is maintained by an independent cybersecurity practitioner and researcher with experience in threat intelligence, security operations, and AI applications in security contexts.
                                </Typography>
                            </Box>
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    bgcolor: alpha(cyber.primary, 0.05),
                                    border: `1px solid ${alpha(cyber.primary, 0.2)}`,
                                    borderRadius: 1,
                                    flex: 1,
                                }}
                            >
                                <EmailIcon sx={{ color: 'primary.main' }} />
                                <Link href="mailto:info@ti-mindmap-hub.com" color="primary" underline="hover">
                                    info@ti-mindmap-hub.com
                                </Link>
                            </Paper>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    bgcolor: alpha(cyber.primary, 0.05),
                                    border: `1px solid ${alpha(cyber.primary, 0.2)}`,
                                    borderRadius: 1,
                                    flex: 1,
                                }}
                            >
                                <XIcon sx={{ color: 'primary.main' }} />
                                <Link href="https://x.com/ti_mindmap_hub" target="_blank" rel="noopener noreferrer" color="primary" underline="hover">
                                    @ti_mindmap_hub
                                </Link>
                            </Paper>
                        </Stack>
                    </SectionCard>
                </Fade>

                {/* Core Features Section */}
                <Fade in timeout={700}>
                    <Box sx={{ mb: 6 }}>
                        <Typography 
                            variant="h4" 
                            fontWeight="bold" 
                            gutterBottom
                            sx={{ 
                                mb: 3,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <AutoAwesomeIcon color="primary" />
                            Core Features
                        </Typography>
                        
                        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
                            {/* Feature 1: Automated Ingestion */}
                            <FeatureCard elevation={2}>
                                <IconWrapper sx={{ background: alpha(cyber.accent.cyan, 0.14) }}>
                                    <SpeedIcon sx={{ fontSize: 28, color: cyber.accent.cyan }} />
                                </IconWrapper>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    Automated Ingestion
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    The HUB automatically processes new articles from a curated list of leading threat intelligence sources or through direct user submissions.
                                </Typography>
                            </FeatureCard>

                            {/* Feature 2: AI-Powered Analysis */}
                            <FeatureCard elevation={2}>
                                <IconWrapper>
                                    <PsychologyIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                                </IconWrapper>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    AI-Powered Analysis
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Using powerful Generative AI models, the application generates:
                                </Typography>
                                <Stack spacing={1}>
                                    <SubFeatureItem>
                                        <Typography variant="body2">• Detailed technical summaries</Typography>
                                    </SubFeatureItem>
                                    <SubFeatureItem>
                                        <Typography variant="body2">• Visual mindmaps for complex threats</Typography>
                                    </SubFeatureItem>
                                    <SubFeatureItem>
                                        <Typography variant="body2">• Extracted Indicators of Compromise (IOCs)</Typography>
                                    </SubFeatureItem>
                                    <SubFeatureItem>
                                        <Typography variant="body2">• TTPs mapped to MITRE ATT&CK</Typography>
                                    </SubFeatureItem>
                                    <SubFeatureItem>
                                        <Typography variant="body2">• "Five Whats" structured reports</Typography>
                                    </SubFeatureItem>
                                    <SubFeatureItem>
                                        <Typography variant="body2">• Probable execution order of TTPs</Typography>
                                    </SubFeatureItem>
                                </Stack>
                            </FeatureCard>

                            {/* Feature 3: Weekly Briefing Agent */}
                            <FeatureCard elevation={2}>
                                <IconWrapper sx={{ background: alpha(cyber.accent.green, 0.14) }}>
                                    <TrendingUpIcon sx={{ fontSize: 28, color: cyber.accent.green }} />
                                </IconWrapper>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    Weekly Briefing Agent
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    A dedicated multi-agent AI system analyzes all weekly intelligence. It identifies emerging trends, highlights frequent TTPs, most targeted sectors, and provides deep dives on significant threats.
                                </Typography>
                            </FeatureCard>

                            {/* Feature 4: Centralized Intelligence */}
                            <FeatureCard elevation={2}>
                                <IconWrapper sx={{ background: alpha(cyber.accent.coral, 0.14) }}>
                                    <SecurityIcon sx={{ fontSize: 28, color: cyber.accent.coral }} />
                                </IconWrapper>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    Centralized Intelligence
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    All processed reports in a centralized, filterable dashboard. Single pane of glass for browsing and searching through a growing library of threat intelligence.
                                </Typography>
                            </FeatureCard>

                            {/* Feature 5: Multiple Data Views & Integration */}
                            <FeatureCard elevation={2} sx={{ gridColumn: { xs: '1', md: 'span 2' } }}>
                                <IconWrapper>
                                    <IntegrationInstructionsIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                                </IconWrapper>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    Multiple Data Views & Integration
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Each report offers various perspectives: raw text, visual mindmaps, structured IOC/TTP tables, and downloadable STIX 2.1 bundles. Seamless integration with SIEMs, SOARs, or Threat Intelligence Platforms (TIPs).
                                </Typography>
                                
                                {/* AI Limitations Note */}
                                <Paper
                                    elevation={0}
                                    sx={{
                                        mt: 2,
                                        p: 2,
                                        bgcolor: (theme) => alpha(theme.palette.warning.main, 0.05),
                                        border: (theme) => `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Note:</strong> All AI-generated outputs are experimental and require human verification before operational use.
                                    </Typography>
                                </Paper>
                            </FeatureCard>
                        </Box>
                    </Box>
                </Fade>

                {/* Use Cases Section */}
                <Fade in timeout={900}>
                    <Box sx={{ mb: 6 }}>
                        <Typography 
                            variant="h4" 
                            fontWeight="bold" 
                            gutterBottom
                            sx={{ 
                                mb: 3,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <GroupsIcon color="primary" />
                            Use Cases
                        </Typography>
                        
                        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
                            <UseCaseCard>
                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                    <ManageSearchIcon sx={{ color: cyber.accent.cyan, fontSize: 32 }} />
                                    <Box>
                                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                                            Threat Analysts
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Quickly digest and understand key takeaways from new threat reports without hours of manual reading.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </UseCaseCard>

                            <UseCaseCard>
                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                    <ShieldIcon sx={{ color: cyber.accent.green, fontSize: 32 }} />
                                    <Box>
                                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                                            Security Operations (SOC)
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Easily extract and utilize IOCs for threat hunting, detection rule creation, and SIEM enrichment.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </UseCaseCard>

                            <UseCaseCard>
                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                    <SecurityIcon sx={{ color: cyber.accent.coral, fontSize: 32 }} />
                                    <Box>
                                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                                            Incident Responders
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Gain rapid context on adversary TTPs during investigations to understand how attackers operate.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </UseCaseCard>

                            <UseCaseCard>
                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                    <BusinessIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                                    <Box>
                                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                                            Security Leadership
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Maintain high-level overview of the threat landscape through accessible summaries and automated weekly briefings.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </UseCaseCard>
                        </Box>
                    </Box>
                </Fade>

                {/* What's Next Section */}
                <Fade in timeout={1100}>
                    <Paper 
                        elevation={3} 
                        sx={{ 
                            p: 4, 
                            background: `linear-gradient(135deg, ${alpha(cyber.primary, 0.05)}, ${alpha(cyber.primaryLight, 0.05)})`,
                            border: `1px solid ${alpha(cyber.primary, 0.2)}`,
                            borderRadius: 2
                        }}
                    >
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                            <RocketLaunchIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                            <Typography variant="h4" fontWeight="bold">
                                What's Next?
                            </Typography>
                        </Stack>
                        
                        <Typography variant="body1" color="text.secondary" paragraph>
                            TI Mindmap HUB is an experimental and research-driven project. We are continuously working to enhance its capabilities, including the development of a <strong>Threat Intelligence Knowledge Graph</strong>.
                        </Typography>
                        
                        <Paper 
                            elevation={0} 
                            sx={{ 
                                p: 3, 
                                mb: 2,
                                bgcolor: alpha(cyber.primary, 0.05),
                                border: `1px solid ${alpha(cyber.primary, 0.2)}`,
                                borderRadius: 2
                            }}
                        >
                            <Stack direction="row" spacing={2} alignItems="flex-start">
                                <AccountTreeIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                                <Box>
                                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                                        Future: Knowledge Graph
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        This future implementation will use Large Language Models to map and visualize complex relationships between threats, actors, malware, and infrastructure, enabling even deeper analysis.
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>

                        <Typography variant="body1" color="text.secondary">
                            We invite you to visit periodically to explore the latest features and the growing repository of threat intelligence. For the full roadmap, visit the <Link href="/roadmap" color="primary">Roadmap page</Link>.
                        </Typography>
                    </Paper>
                </Fade>
            </Container>
        </Box>
    );
};

export default About;